import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fetchUsersWithStats, fetchUserDetailedStats, UserWithStats } from '../api/analytics';

const COLORS = {
  primary: '#10b981', secondary: '#3b82f6', accent: '#8b5cf6',
  warning: '#f59e0b', success: '#22c55e', purple: '#a855f7',
};

interface UserDetailsModalProps {
  userId: number | null;
  onClose: () => void;
}

const UserDetailsModal = ({ userId, onClose }: UserDetailsModalProps) => {
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      setLoading(true);
      fetchUserDetailedStats(userId).then(setDetails).finally(() => setLoading(false));
    }
  }, [userId]);

  if (!userId) return null;

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
      <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', maxWidth: '900px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '24px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#f1f5f9', fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>User Details</h2>
            <p style={{ color: '#64748b', fontSize: '13px' }}>{loading ? 'Loading...' : details?.name}</p>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '28px', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>⏳ Loading...</div>
          ) : details ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155' }}>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Email</div>
                  <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{details.email}</div>
                </div>
                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155' }}>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Nickname</div>
                  <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{details.nickname}</div>
                </div>
                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155' }}>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Joined</div>
                  <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{format(new Date(details.createdAt), 'MMM dd, yyyy')}</div>
                </div>
                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155' }}>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Status</div>
                  <span style={{ background: details.isConfirmed ? COLORS.success : '#334155', color: '#fff', padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>{details.isConfirmed ? 'Confirmed' : 'Unconfirmed'}</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                {[
                  { label: 'Hops Created', value: details.summary?.hopsCreated || 0, color: COLORS.primary },
                  { label: 'Hop Sessions', value: details.summary?.totalHopSessions || 0, color: COLORS.secondary },
                  { label: 'Games Played', value: details.summary?.sessionsPlayed || 0, color: COLORS.accent },
                  { label: 'Completed', value: details.summary?.sessionsCompleted || 0, color: COLORS.warning },
                  { label: 'Wins', value: details.summary?.wins || 0, color: COLORS.success },
                  { label: 'Win Rate', value: `${details.summary?.winRate || 0}%`, color: COLORS.purple },
                ].map((stat) => (
                  <div key={stat.label} style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155', textAlign: 'center' }}>
                    <div style={{ color: stat.color, fontSize: '24px', fontWeight: '700' }}>{stat.value}</div>
                    <div style={{ color: '#94a3b8', fontSize: '11px' }}>{stat.label}</div>
                  </div>
                ))}
              </div>
              {details.createdHops?.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Created Hops ({details.createdHops.length})</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {details.createdHops.slice(0, 5).map((hop: any) => (
                      <div key={hop.id} style={{ background: '#0f172a', padding: '12px 16px', borderRadius: '8px', border: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{hop.name}</div>
                          <div style={{ color: '#64748b', fontSize: '12px' }}>{hop._count?.sessions || 0} sessions • {hop.isPublic ? 'Public' : 'Private'}</div>
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: '12px' }}>{format(new Date(hop.createdAt), 'MMM dd, yyyy')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {details.recentSessions?.length > 0 && (
                <div>
                  <h3 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Recent Sessions ({details.recentSessions.length})</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {details.recentSessions.slice(0, 10).map((session: any) => (
                      <div key={session.sessionId} style={{ background: '#0f172a', padding: '12px 16px', borderRadius: '8px', border: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{session.hopName}</div>
                          <div style={{ color: '#64748b', fontSize: '12px' }}>{session.teamName || 'No team'} • {session.status}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {session.won && <span style={{ background: COLORS.success, color: '#fff', padding: '2px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: '600' }}>WON</span>}
                          <div style={{ color: '#94a3b8', fontSize: '12px' }}>{format(new Date(session.playedAt), 'MMM dd, HH:mm')}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>User not found</div>}
        </div>
      </div>
    </div>
  );
};

export default function Users() {
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const limit = 20;

  useEffect(() => {
    setLoading(true);
    fetchUsersWithStats(limit, page * limit, search || undefined)
      .then((res) => { setUsers(res.users); setTotal(res.total); })
      .finally(() => setLoading(false));
  }, [page, search]);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', padding: '32px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ color: '#f1f5f9', fontSize: '32px', fontWeight: '800', marginBottom: '8px' }}>👥 Registered Users</h1>
            <p style={{ color: '#64748b', fontSize: '14px' }}>{total} total users</p>
          </div>
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search users..." style={{ padding: '12px 20px', background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', color: '#f1f5f9', fontSize: '14px', width: '300px' }} />
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>⏳ Loading users...</div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>No users found</div>
        ) : (
          <>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '16px', border: '1px solid #334155', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#0f172a' }}>
                    {['User', 'Joined', 'Hops', 'Sessions', 'Played', 'Wins', 'Win Rate', 'Status'].map((h) => (
                      <th key={h} style={{ padding: '16px', textAlign: 'left', color: '#94a3b8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', borderBottom: '1px solid #334155' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} onClick={() => setSelectedUserId(user.id)} style={{ cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#1e293b'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '16px', borderBottom: '1px solid #334155' }}>
                        <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{user.name}</div>
                        <div style={{ color: '#64748b', fontSize: '12px' }}>{user.email}</div>
                      </td>
                      <td style={{ padding: '16px', color: '#94a3b8', fontSize: '13px', borderBottom: '1px solid #334155' }}>{format(new Date(user.createdAt), 'MMM dd, yyyy')}</td>
                      <td style={{ padding: '16px', color: COLORS.primary, fontSize: '14px', fontWeight: '600', borderBottom: '1px solid #334155' }}>{user.stats.hopsCreated}</td>
                      <td style={{ padding: '16px', color: COLORS.secondary, fontSize: '14px', fontWeight: '600', borderBottom: '1px solid #334155' }}>{user.stats.totalHopSessions}</td>
                      <td style={{ padding: '16px', color: COLORS.accent, fontSize: '14px', fontWeight: '600', borderBottom: '1px solid #334155' }}>{user.stats.sessionsPlayed}</td>
                      <td style={{ padding: '16px', color: COLORS.success, fontSize: '14px', fontWeight: '600', borderBottom: '1px solid #334155' }}>{user.stats.wins}</td>
                      <td style={{ padding: '16px', borderBottom: '1px solid #334155' }}>
                        <span style={{ background: user.stats.winRate >= 50 ? COLORS.success : user.stats.winRate >= 25 ? COLORS.warning : '#334155', color: '#fff', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '600' }}>{user.stats.winRate}%</span>
                      </td>
                      <td style={{ padding: '16px', borderBottom: '1px solid #334155' }}>
                        <span style={{ background: user.isConfirmed ? COLORS.success : '#334155', color: '#fff', padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' }}>{user.isConfirmed ? '✓' : '○'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
              <div style={{ color: '#64748b', fontSize: '14px' }}>Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} style={{ padding: '8px 16px', background: page === 0 ? '#1e293b' : COLORS.primary, color: page === 0 ? '#64748b' : '#0f172a', border: 'none', borderRadius: '8px', cursor: page === 0 ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}>Previous</button>
                <button onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= total} style={{ padding: '8px 16px', background: (page + 1) * limit >= total ? '#1e293b' : COLORS.primary, color: (page + 1) * limit >= total ? '#64748b' : '#0f172a', border: 'none', borderRadius: '8px', cursor: (page + 1) * limit >= total ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
      {selectedUserId && <UserDetailsModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />}
    </div>
  );
}

